import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Button, Stack,
  Chip, List, ListItem, ListItemText, Avatar, Divider, LinearProgress,
  Alert, IconButton, Tooltip, TextField, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import {
  MY_COMMUNICATION_THREADS_QUERY, COMMUNICATION_THREAD_QUERY,
  CREATE_COMMUNICATION_THREAD_MUTATION, UPDATE_COMMUNICATION_THREAD_MUTATION,
  DELETE_COMMUNICATION_THREAD_MUTATION, SEND_COMMUNICATION_MESSAGE_MUTATION,
  DELETE_COMMUNICATION_MESSAGE_MUTATION,
} from '../graphql/queries';
import { useAuth } from '../context/AuthContext';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const THREAD_TYPES = ['general','vendor','family','emergency'];
const THREAD_COLOR = { general:'primary', vendor:'secondary', family:'info', emergency:'error' };

const EMPTY_THREAD = { title:'', description:'', threadType:'general' };

export default function CommunicationPage() {
  const { user: me } = useAuth();
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMsg, setNewMsg]       = useState('');
  const [isEmergency, setIsEmergency] = useState(false);
  const [error, setError]         = useState('');

  const [threadDialog, setThreadDialog] = useState(false);
  const [editThread, setEditThread]     = useState(null);
  const [threadForm, setThreadForm]     = useState(EMPTY_THREAD);

  const { data: listData, loading: listLoading, refetch: refetchList } = useQuery(MY_COMMUNICATION_THREADS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: threadData, loading: threadLoading, refetch: refetchThread } = useQuery(COMMUNICATION_THREAD_QUERY, {
    variables: { id: selectedThread },
    skip: !selectedThread,
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000,
  });

  const threads = listData?.myCommunicationThreads || [];
  const active  = threadData?.communicationThread;

  const [createThread] = useMutation(CREATE_COMMUNICATION_THREAD_MUTATION, { onCompleted: () => { setThreadDialog(false); setThreadForm(EMPTY_THREAD); refetchList(); setError(''); }, onError: e => setError(e.message) });
  const [updateThread] = useMutation(UPDATE_COMMUNICATION_THREAD_MUTATION, { onCompleted: () => { setThreadDialog(false); setEditThread(null); refetchList(); setError(''); }, onError: e => setError(e.message) });
  const [deleteThread] = useMutation(DELETE_COMMUNICATION_THREAD_MUTATION, { onCompleted: () => { setSelectedThread(null); refetchList(); } });
  const [sendMsg]      = useMutation(SEND_COMMUNICATION_MESSAGE_MUTATION, {
    onCompleted: () => { setNewMsg(''); setIsEmergency(false); refetchThread(); },
    onError: e => setError(e.message),
  });
  const [deleteMsg] = useMutation(DELETE_COMMUNICATION_MESSAGE_MUTATION, { onCompleted: () => refetchThread() });

  const openCreateThread = () => { setEditThread(null); setThreadForm(EMPTY_THREAD); setThreadDialog(true); };
  const openEditThread   = (t) => { setEditThread(t); setThreadForm({ title: t.title, description: t.description || '', threadType: t.threadType }); setThreadDialog(true); };
  const handleSaveThread = () => {
    if (editThread) updateThread({ variables: { id: editThread.id, ...threadForm } });
    else createThread({ variables: threadForm });
  };
  const handleSendMsg = () => {
    if (!newMsg.trim()) return;
    sendMsg({ variables: { threadId: selectedThread, message: newMsg.trim(), isEmergency } });
  };

  return (
    <Box>
      {!selectedThread ? (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" fontWeight={700}>Communication</Typography>
              <Typography variant="body2" color="text.secondary">Coordination channels for families, vendors, and emergency alerts.</Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateThread}>New Thread</Button>
          </Stack>
          {listLoading && <LinearProgress sx={{ mb: 2 }} />}
          {threads.length === 0 && !listLoading && <Alert severity="info">No communication threads yet. Click <strong>New Thread</strong> to start.</Alert>}
          <Grid container spacing={2}>
            <Paged items={threads}>{rows => rows.map(t => (
              <Grid item xs={12} sm={6} md={4} key={t.id}>
                <Card sx={{ borderLeft: `4px solid`, borderColor: t.threadType === 'emergency' ? 'error.main' : 'primary.main', opacity: t.isActive ? 1 : 0.6 }}>
                  <CardActionArea onClick={() => setSelectedThread(t.id)} sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Chip label={t.threadType} size="small" color={THREAD_COLOR[t.threadType] || 'default'} />
                      {!t.isActive && <Chip label="Archived" size="small" />}
                    </Stack>
                    <Typography fontWeight={700} gutterBottom>{t.title}</Typography>
                    {t.description && <Typography variant="body2" color="text.secondary" noWrap>{t.description}</Typography>}
                    <Typography variant="caption" color="text.secondary">{t.messageCount || 0} messages</Typography>
                  </CardActionArea>
                  <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ px: 1, pb: 1 }}>
                    <Tooltip title="Edit"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditThread(t); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteThread({ variables: { id: t.id } }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </>
      ) : (
        <>
          {/* Thread view */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <IconButton onClick={() => setSelectedThread(null)}><ArrowBackIcon /></IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" fontWeight={700}>{active?.title}</Typography>
              <Stack direction="row" spacing={1}>
                {active && <Chip label={active.threadType} size="small" color={THREAD_COLOR[active.threadType] || 'default'} />}
                {active?.description && <Typography variant="body2" color="text.secondary">{active.description}</Typography>}
              </Stack>
            </Box>
          </Stack>

          {threadLoading && <LinearProgress sx={{ mb: 1 }} />}
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

          {/* Messages */}
          <Paper variant="outlined" sx={{ minHeight: 400, maxHeight: 500, overflow: 'auto', p: 2, mb: 2 }}>
            {(active?.messages || []).length === 0 && <Typography variant="body2" color="text.secondary">No messages yet. Send the first message below.</Typography>}
            {(active?.messages || []).map((msg, idx) => {
              const isMine = msg.senderId === me?.id;
              return (
                <Box key={msg.id} sx={{ mb: 1.5, display:'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                  <Box>
                    {!isMine && <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{msg.sender?.email}</Typography>}
                    <Paper sx={{
                      p: 1.5,
                      maxWidth: 400,
                      bgcolor: isMine ? 'primary.main' : msg.isEmergency ? 'error.light' : 'action.hover',
                      color: isMine ? 'white' : 'text.primary',
                      borderRadius: isMine ? '12px 12px 0 12px' : '12px 12px 12px 0',
                    }}>
                      {msg.isEmergency && <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}><WarningIcon fontSize="small" color="warning" /><Typography variant="caption" fontWeight={700} color="warning.main">EMERGENCY</Typography></Stack>}
                      <Typography variant="body2">{msg.message}</Typography>
                    </Paper>
                    <Typography variant="caption" color="text.secondary" sx={{ display:'block', textAlign: isMine ? 'right' : 'left', mt: 0.25 }}>
                      {new Date(msg.createdAt).toLocaleTimeString()}
                      {isMine && (
                        <IconButton size="small" sx={{ ml: 0.5 }} onClick={() => deleteMsg({ variables: { id: msg.id } })}><DeleteIcon sx={{ fontSize: 12 }} /></IconButton>
                      )}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Paper>

          {/* Message input */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Stack sx={{ flexGrow: 1 }} spacing={0.5}>
              <TextField
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMsg(); } }}
                placeholder="Type a message (Enter to send)"
                fullWidth
                multiline
                maxRows={3}
                size="small"
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label="Emergency"
                  size="small"
                  color={isEmergency ? 'error' : 'default'}
                  onClick={() => setIsEmergency(v => !v)}
                  icon={<WarningIcon />}
                  variant={isEmergency ? 'filled' : 'outlined'}
                />
                <Typography variant="caption" color="text.secondary">Shift+Enter for new line</Typography>
              </Stack>
            </Stack>
            <Button variant="contained" endIcon={<SendIcon />} onClick={handleSendMsg} disabled={!newMsg.trim()} sx={{ height: 40 }}>Send</Button>
          </Stack>
        </>
      )}

      {/* Thread Dialog */}
      <Dialog open={threadDialog} onClose={() => setThreadDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editThread ? 'Edit Thread' : 'New Communication Thread'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Thread Title" value={threadForm.title} onChange={e => setThreadForm(f => ({ ...f, title: e.target.value }))} fullWidth required />
            <TextField select label="Type" value={threadForm.threadType} onChange={e => setThreadForm(f => ({ ...f, threadType: e.target.value }))} fullWidth>
              {THREAD_TYPES.map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
            </TextField>
            <TextField label="Description" value={threadForm.description} onChange={e => setThreadForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setThreadDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveThread}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
